import styled, { keyframes, createGlobalStyle } from 'styled-components';

// Keyframes para animações
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const expandWidth = keyframes`
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
`;

export const GlobalStyle = createGlobalStyle`
  :root {
    --color-primary-a: #009bd9;
    --color-primary-b: #024995;
    --color-primary-c: #f39200;
    --color-support: #a4acb1;
    --color-primary-a-rgb: 0, 155, 217;
    --color-primary-b-rgb: 2, 73, 149;
    --color-primary-c-rgb: 243, 146, 0;
    --color-support-rgb: 164, 172, 177;
  }
`;

// Container principal
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

// Header e navegação
export const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #024995 0%, #009bd9 60%, #009bd9 100%);
  color: white;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const Logo = styled.div`
  a {
    display: flex;
    align-items: center;
    align-self: center;
    text-decoration: none;
    background: white;
    border-radius: 10px;
    padding: 5px 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transition: opacity 0.2s ease;
    
    img {
      height: 36px;
      width: auto;
      display: block;
      object-fit: contain;

      @media (max-width: 768px) {
        height: 30px;
      }

      @media (max-width: 480px) {
        height: 26px;
      }
    }

    &:hover {
      opacity: 0.85;
    }
  }
`;

export const Nav = styled.nav`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  width: 100%;
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr auto;
    gap: 1rem;
  }
`;

export const NavLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  justify-self: center;
  
  a {
    text-decoration: none;
    color: white;
    font-weight: 500;
    transition: color 0.3s ease;
    white-space: nowrap;
    font-size: 0.95rem;
    
    &:hover {
      color: var(--color-primary-c);
    }
  }

  @media (max-width: 1500px) {
    gap: 1rem;

    a {
      font-size: 0.9rem;
    }
  }

  @media (max-width: 1440px) {
    gap: 0.75rem;

    a {
      font-size: 0.85rem;
    }
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

export const NavLink = styled(({ isActive, ...props }) => <a {...props} />)`
  color: white;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  position: relative;
  padding: 0.5rem 0;
  transition: all 0.3s ease;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: white;
    transition: width 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    
    &::after {
      width: 100%;
    }
  }
  
  ${({ isActive }) => isActive && `
    &::after {
      width: 100%;
    }
  `}
`;

export const LoginButton = styled.button`
  background: white;
  color: var(--color-primary-b);
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  justify-self: end;
  
  &:hover {
    background: #f8f9fa;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(var(--color-primary-a-rgb), 0.2);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

export const MobileLoginButton = styled.button`
  background: white;
  color: var(--color-primary-b);
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f8f9fa;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(var(--color-primary-a-rgb), 0.2);
  }
`;

// Menu mobile
export const MobileMenuContainer = styled.div`
  display: none;
  position: relative;
  justify-self: end;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

interface HamburgerButtonProps {
  open: boolean;
}

export const HamburgerButton = styled.button<HamburgerButtonProps>`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 2rem;
  height: 2rem;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 10;
  
  span {
    width: 2rem;
    height: 0.25rem;
    background: white;
    border-radius: 10px;
    transition: all 0.3s linear;
    position: relative;
    transform-origin: 1px;
    
    &:first-child {
      transform: ${({ open }) => (open ? 'rotate(45deg)' : 'rotate(0)')};
    }
    
    &:nth-child(2) {
      opacity: ${({ open }) => (open ? '0' : '1')};
      transform: ${({ open }) => (open ? 'translateX(20px)' : 'translateX(0)')};
    }
    
    &:nth-child(3) {
      transform: ${({ open }) => (open ? 'rotate(-45deg)' : 'rotate(0)')};
    }
  }
`;

interface MobileMenuProps {
  isOpen: boolean;
}

export const MobileMenu = styled.div<MobileMenuProps>`
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  flex-direction: column;
  background: linear-gradient(160deg, #024995 0%, #009bd9 100%);
  position: absolute;
  top: 60px;
  right: 0;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255,255,255,0.08);
  width: 220px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  animation: ${scaleIn} 0.3s ease-out forwards;
  z-index: 5;
`;

export const MobileMenuLink = styled.button`
  text-decoration: none;
  color: rgba(255, 255, 255, 0.88);
  font-weight: 500;
  font-size: 0.95rem;
  margin: 0;
  transition: all 0.2s ease;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  text-align: left;
  cursor: pointer;
  padding: 0.65rem 0.5rem;
  width: 100%;
  
  &:hover {
    color: white;
    padding-left: 0.75rem;
  }
`;

// Seção do E-book
export const EbookSection = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  position: relative;
  flex: 1;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(var(--color-primary-a-rgb), 0.05) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(var(--color-primary-c-rgb), 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

export const EbookContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
    text-align: center;
  }
`;

export const EbookImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  img {
    width: 100%;
    max-width: 400px;
    height: auto;
    border-radius: 20px;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-10px) scale(1.02);
      box-shadow: 0 30px 60px rgba(0, 0, 0, 0.2);
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    right: -20px;
    bottom: -20px;
    background: linear-gradient(135deg, rgba(var(--color-primary-a-rgb), 0.1) 0%, rgba(var(--color-primary-c-rgb), 0.1) 100%);
    border-radius: 30px;
    z-index: -1;
    animation: pulse 3s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 0.7;
    }
    50% {
      transform: scale(1.05);
      opacity: 0.3;
    }
  }
`;

export const EbookTextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

export const EbookTitle = styled.h2`
  font-size: 3rem;
  font-weight: 900;
  background: linear-gradient(135deg, var(--color-primary-a) 0%, var(--color-primary-b) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: -0.5px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

export const EbookSubtitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 1rem;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

export const EbookDescription = styled.p`
  font-size: 1.1rem;
  color: #4a5568;
  line-height: 1.7;
  margin-bottom: 1.5rem;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

export const EbookHighlight = styled.div`
  background: linear-gradient(135deg, var(--color-primary-c) 0%, rgba(var(--color-primary-c-rgb), 0.9) 100%);
  color: #2d3748;
  padding: 1.5rem;
  border-radius: 15px;
  font-weight: 600;
  font-size: 1.1rem;
  text-align: center;
  box-shadow: 0 8px 25px rgba(var(--color-primary-c-rgb), 0.3);
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.6s ease;
  }

  &:hover::before {
    left: 100%;
  }
`;

export const EbookButton = styled.button`
  background: linear-gradient(135deg, var(--color-primary-a) 0%, var(--color-primary-b) 100%);
  color: white;
  border: none;
  padding: 1.5rem 3rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  text-transform: uppercase;
  letter-spacing: 1px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(var(--color-primary-a-rgb), 0.3);
  align-self: flex-start;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.6s ease;
  }

  &:hover {
    background: linear-gradient(135deg, var(--color-primary-b) 0%, var(--color-primary-a) 100%);
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 15px 35px rgba(var(--color-primary-a-rgb), 0.4), 0 5px 15px rgba(0, 0, 0, 0.1);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px) scale(1.02);
  }

  @media (max-width: 768px) {
    align-self: center;
    width: 100%;
    max-width: 300px;
  }
`;

// Footer
export const Footer = styled.footer`
  background:
    radial-gradient(circle at 30% 50%, rgba(0, 155, 217, 0.28) 0%, transparent 55%),
    linear-gradient(135deg, #024995 0%, #009bd9 60%, #024995 100%);
  color: white;
  padding: 4rem 2rem 1rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(var(--color-primary-a-rgb), 0.08) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(var(--color-primary-c-rgb), 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

export const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 3rem;
  position: relative;
  z-index: 1;
`;

export const FooterSection = styled.div`
  h4 {
    font-size: 1.3rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    background: linear-gradient(135deg, #ffc107 0%, #ff9800 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  p, a {
    color: #cbd5e0;
    text-decoration: none;
    line-height: 1.7;
    display: block;
    margin-bottom: 0.8rem;
    font-weight: 400;
    transition: all 0.3s ease;
    
    &:hover {
      color: #ffc107;
      transform: translateX(4px);
    }
  }
`;

export const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  
  img {
    height: 58px;
    width: auto;
    object-fit: contain;
    opacity: 0.92;
    transition: opacity 0.2s ease;
    
    &:hover {
      opacity: 1;
    }
  }
`;

export const FooterBottom = styled.div`
  max-width: 1200px;
  margin: 3rem auto 0;
  padding-top: 2.5rem;
  border-top: 1px solid rgba(255, 193, 7, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #cbd5e0;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

export const SocialIcons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 0rem;
  
  @media (max-width: 768px) {
    gap: 1rem;
    margin-top: 0.8rem;
  }
`;

export const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: #cbd5e0;
  text-decoration: none;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  svg {
    width: 24px;
    height: 24px;
    transition: all 0.3s ease;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: #ffc107;
    transform: translateY(-3px) scale(1.1);
    box-shadow: 0 8px 25px rgba(255, 193, 7, 0.3);
    
    svg {
      transform: scale(1.1);
    }
  }
  
  &:active {
    transform: translateY(-1px) scale(1.05);
  }
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;